import React, { useState, lazy, Suspense } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { QueryStringProvider } from './Contexts/QueryString';
import { VisitorParamsProvider } from './Contexts/VisitorParams';
import { queryString } from './Utility/QueryString';
import { RedirectProvider } from './Contexts/RedirectContext';
import './App.scss';
import LoadingFallback from './Components/Pages/LoadingFallback';
import { isStartDomain, isHelpDomain } from './Utility/DomainBasedRoutes';

const ScheduleMeeting = lazy(
  () => import('./Components/Pages/Loader/ScheduleMeeting')
);
const HomePage = lazy(() => import('./Components/Pages/HomePage'));
const FbPixel = lazy(() => import('./Components/Pages/FbPixel'));
const NotFound = lazy(() => import('./Components/Pages/NotFound'));

const ThankYouV1 = lazy(() => import('./Components/Pages/CSSD_V3/ThankYouV1'));
const PaymentForm = lazy(
  () => import('./Components/Pages/CSSD_V3/PaymentForm')
);
const PaymentResponse = lazy(
  () => import('./Components/Pages/CSSD_V3/PaymentResponse')
);
const SignatureV1 = lazy(
  () => import('./Components/Pages/CMSH_V1/SignatureV1')
);
const AdtopiaLoadDynamic = lazy(() => import('./Utility/AdtopiaLoadDynamic'));
const EnvDocView = lazy(() => import('./Components/Pages/Docs/EnvDocView'));

const scheduledMaintenance = React.lazy(
  () => import('./Components/Pages/scheduledMaintenance')
);
const QuestionSectionV3 = React.lazy(
  () => import('./Components/Pages/CSSD_V3/PersonalDetails')
);
const QuestionSectionV4 = React.lazy(
  () => import('./Components/Pages/V4/PersonalDetails')
);
const QuestionSection = React.lazy(
  () => import('./Components/Pages/CSSD_V3/QuestionSection')
);
const CSSD_V3 = React.lazy(() => import('./Components/Pages/Splits/CSSD_V3'));
const V4 = React.lazy(() => import('./Components/Pages/Splits/V4'));
const Volunteer = React.lazy(
  () => import('./Components/Pages/LongForm/Volunteer')
);
const Leader = lazy(() => import('./Components/Pages/VolunteerForm/Leader'));
const CalendlyLoader = React.lazy(
  () => import('./Components/Pages/CSSD_V3/CalendlyLoader')
);

const LongForm = React.lazy(() => import('./Components/Pages/Loader/LongForm'));
const LongFormV2 = React.lazy(
  () => import('./Components/Pages/Loader/LongFormV2')
);
const AllyLongForm = React.lazy(
  () => import('./Components/Pages/AllyLongForm/AllyLongForm')
);
const EventInvite = React.lazy(
  () => import('./Components/Pages/Loader/EventInvite')
);
const EventsThankYou = lazy(() => import('./Components/Pages/Events/ThankYou'));
const Info = lazy(() => import('./Components/Pages/Info/Info'));
const VolunteerOpportunities = lazy(
  () => import('./Components/Pages/VolunteerOpportunities')
);
const BecomeCircleLeader = lazy(
  () => import('./Components/Pages/BecomeCircleLeader')
);
const BecomeCircleAlly = lazy(
  () => import('./Components/Pages/BecomeCircleAlly')
);
const CommunityBuilder = lazy(
  () => import('./Components/Pages/CommunityBuilder/CommunityBuilder')
);
const ThankYouV5 = lazy(
  () => import('./Components/Pages/CommunityBuilder/ThankYouV5')
);
const Donate = lazy(() => import('./Components/Pages/Donate/Donate'));
const DonateThankYou = lazy(
  () => import('./Components/Pages/Donate/DonateThankYou')
);
const PovertyQuiz = lazy(
  () => import('./Components/Pages/PovertyQuiz/PovertyQuiz')
);

const RedirectStartDomain = lazy(
  () => import('./Components/Pages/RedirectStartDomain')
);

const CommunityBuilderV2 = lazy(
  () => import('./Components/Pages/CommunityBuilderV2/CommunityBuilder')
);

const App = () => {
  const [visitorParameters] = useState({ visitor_parameters: [], data: [] });
  const [redirectUrl] = useState({ redirectUrl: '' });

  return (
    <>
      <VisitorParamsProvider value={{ visitorParameters }}>
        <QueryStringProvider value={{ queryString }}>
          <RedirectProvider value={{ redirectUrl }}>
            <BrowserRouter>
              <Suspense fallback={<LoadingFallback />}>
                <Switch>
                  {isHelpDomain() && (
                    <>
                      <Route exact path="/" component={HomePage} />
                      <Route exact path="/fbpixel" component={FbPixel} />
                      <Route exact path="/404" component={NotFound} />
                      <Route
                        exact
                        path="/V1/signature"
                        component={SignatureV1}
                      />
                      <Route exact path="/V3/payment" component={PaymentForm} />
                      <Route exact path="/V3/thankyou" component={ThankYouV1} />
                      <Route
                        exact
                        path="/V3/payment-response"
                        component={PaymentResponse}
                      />
                      <Route
                        exact
                        path={['/google', '/testing']}
                        component={AdtopiaLoadDynamic}
                      />
                      <Route
                        exact
                        path="/doc/:docType/:filename"
                        component={EnvDocView}
                      />
                      <Route
                        exact
                        path="/scheduled-maintenance"
                        component={scheduledMaintenance}
                      />
                      <Route
                        exact
                        path="/quiz/v3/questionnaires"
                        component={QuestionSectionV3}
                      />
                      <Route
                        exact
                        path="/quiz/v4/questionnaires"
                        component={QuestionSectionV4}
                      />
                      <Route exact path="/quiz" component={QuestionSection} />
                      <Route
                        exact
                        path="/support_our_cause"
                        component={CSSD_V3}
                      />
                      <Route exact path="/V4" component={V4} />

                      <Route
                        exact
                        path="/calendly_loader"
                        component={CalendlyLoader}
                      />
                      <Route
                        exact
                        path="/schedule-meeting"
                        component={ScheduleMeeting}
                      />
                      <Route exact path="/long-form" component={LongForm} />

                      <Route
                        exact
                        path="/event-invite"
                        component={EventInvite}
                      />
                      <Route
                        exact
                        path="/events/thankyou"
                        component={EventsThankYou}
                      />
                      <Route
                        exact
                        path="/community_builder"
                        component={CommunityBuilder}
                      />

                      <Route exact path="/V5/thankyou" component={ThankYouV5} />
                      <Route exact path="/image.png" component={Info} />

                      <Route exact path="/community_builder_v2" component={CommunityBuilderV2} />
                      <Route
                        exact
                        path="/poverty-quiz"
                        component={PovertyQuiz}
                      />
                    </>
                  )}

                  {isStartDomain() && (
                    <>
                      <Route
                        exact
                        path="/circles-Sandiego-ally"
                        component={AllyLongForm}
                      />
                      <Route
                        exact
                        path="/circles-sandiego-leader"
                        component={Leader}
                      />
                      <Route
                        exact
                        path="/Circles-Sandiego-Volunteer"
                        component={Volunteer}
                      />
                      <Route
                        exact
                        path="/long-form-v2"
                        component={LongFormV2}
                      />
                      <Route
                        exact
                        path="/volunteer-opportunities"
                        component={VolunteerOpportunities}
                      />
                      <Route
                        exact
                        path="/become-circle-leader"
                        component={BecomeCircleLeader}
                      />
                      <Route
                        exact
                        path="/become-circle-ally"
                        component={BecomeCircleAlly}
                      />
                      <Route exact path="/V3/thankyou" component={ThankYouV1} />

                      <Route exact path="/" component={RedirectStartDomain} />
                    </>
                  )}
                  <Route exact path="/donate" component={Donate} />
                  <Route
                    exact
                    path="/donate/thankyou"
                    component={DonateThankYou}
                  />
                  <Route component={NotFound} />
                </Switch>
              </Suspense>
            </BrowserRouter>
          </RedirectProvider>
        </QueryStringProvider>
      </VisitorParamsProvider>
    </>
  );
};

export default App;
